import { useRef } from "react";
import type { ImageCanvasObject } from "@refinement-canvas/domain";
import { useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";

interface Props {
  readonly object: ImageCanvasObject;
}

// 画像オブジェクト。PNG / JPG / GIF / WebP に対応する。
// loading="lazy" を付与して、ビューポート外の画像はネットワーク取得を遅延させる。
// クリックで選択、ドラッグで移動できる。
export function ImageObject({ object }: Props): JSX.Element {
  const ref = useRef<HTMLImageElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  const isSelected = useCanvasStore((s) =>
    s.selectedObjectIds.length === 1 && s.selectedObjectIds[0] === object.id
  );

  useObjectDrag({
    objectId: object.id,
    elementRef: ref as unknown as React.RefObject<HTMLElement | null>,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialRotation: object.rotation,
    disabled: activeTool === "draw-pen",
  });

  return (
    <img
      ref={ref}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="image"
      src={object.src}
      alt={object.alt ?? ""}
      loading="lazy"
      decoding="async"
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${object.h}px`,
        zIndex: object.zOrder,
        objectFit: "contain",
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        transformOrigin: "center",
        userSelect: "none",
        pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
        cursor: "move",
        outline: isSelected ? "2px solid var(--color-accent)" : "none",
      }}
      draggable={false}
    />
  );
}
